/**
 * User Management Component - Create, edit, delete users
 */
import React, { useState, useEffect } from 'react';
import { useAuthStore, User, UserRole, ModuleAccess } from '../../stores/authStore';
import { getErrorMessage } from '../../utils/errorMessages';
import './AdminPanel.css';

const API_URL = process.env.REACT_APP_API_URL || '';

interface NewUser {
  email: string;
  username: string;
  full_name: string;
  password: string;
  role: UserRole;
}

export const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddUser, setShowAddUser] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [managingModulesUser, setManagingModulesUser] = useState<User | null>(null);

  const { token } = useAuthStore();

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/auth/users`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Nepodarilo sa načítať používateľov');
      }

      const data = await response.json();
      setUsers(data);
      setError(null);
    } catch (err: any) {
      setError(getErrorMessage(err, 'Nepodarilo sa načítať používateľov.'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId: number) => {
    if (!window.confirm('Naozaj chcete vymazať tohto používateľa?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/auth/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Nepodarilo sa vymazať používateľa');
      }

      await fetchUsers();
    } catch (err: any) {
      alert(getErrorMessage(err, 'Nepodarilo sa vymazať používateľa.'));
    }
  };

  const getRoleLabel = (role: UserRole): string => {
    const labels: Record<UserRole, string> = {
      admin: 'Administrátor',
      engineer: 'Inžinier',
      service_tech: 'Servisný technik',
      install_tech: 'Montážny technik',
      viewer: 'Pozorovateľ',
      guest: 'Hosť',
    };
    return labels[role];
  };

  const getRoleBadgeClass = (role: UserRole): string => {
    const classes: Record<UserRole, string> = {
      admin: 'role-badge-admin',
      engineer: 'role-badge-engineer',
      service_tech: 'role-badge-service-tech',
      install_tech: 'role-badge-install-tech',
      viewer: 'role-badge-viewer',
      guest: 'role-badge-guest',
    };
    return classes[role];
  };

  if (loading) {
    return <div className="loading">Načítavam používateľov...</div>;
  }

  if (error) {
    return <div className="error-message">⚠️ {error}</div>;
  }

  return (
    <div className="user-management">
      <div className="management-header">
        <h3>👥 Správa používateľov</h3>
        <button
          className="btn-primary"
          onClick={() => setShowAddUser(true)}
        >
          + Pridať používateľa
        </button>
      </div>

      <div className="users-table">
        <table>
          <thead>
            <tr>
              <th>Email</th>
              <th>Používateľské meno</th>
              <th>Meno</th>
              <th>Rola</th>
              <th>Stav</th>
              <th>Akcie</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id}>
                <td>{user.email}</td>
                <td>{user.username}</td>
                <td>{user.full_name || '-'}</td>
                <td>
                  <span className={`role-badge ${getRoleBadgeClass(user.role)}`}>
                    {getRoleLabel(user.role)}
                  </span>
                </td>
                <td>
                  <span className={`status-badge ${user.is_active ? 'status-active' : 'status-inactive'}`}>
                    {user.is_active ? '✓ Aktívny' : '✗ Neaktívny'}
                  </span>
                </td>
                <td>
                  <div className="action-buttons">
                    <button
                      className="btn-icon"
                      onClick={() => setEditingUser(user)}
                      title="Upraviť"
                    >
                      ✏️
                    </button>
                    <button
                      className="btn-icon"
                      onClick={() => setManagingModulesUser(user)}
                      title="Spravovať moduly"
                    >
                      🧩
                    </button>
                    <button
                      className="btn-icon btn-danger"
                      onClick={() => handleDeleteUser(user.id)}
                      title="Vymazať"
                    >
                      🗑️
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showAddUser && (
        <AddUserDialog
          onClose={() => setShowAddUser(false)}
          onSuccess={() => {
            setShowAddUser(false);
            fetchUsers();
          }}
        />
      )}

      {editingUser && (
        <EditUserDialog
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSuccess={() => {
            setEditingUser(null);
            fetchUsers();
          }}
        />
      )}

      {managingModulesUser && (
        <ManageModulesDialog
          user={managingModulesUser}
          onClose={() => setManagingModulesUser(null)}
          onSuccess={() => {
            setManagingModulesUser(null);
            fetchUsers();
          }}
        />
      )}
    </div>
  );
};

// Add User Dialog Component
interface AddUserDialogProps {
  onClose: () => void;
  onSuccess: () => void;
}

const AddUserDialog: React.FC<AddUserDialogProps> = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState<NewUser>({
    email: '',
    username: '',
    full_name: '',
    password: '',
    role: 'viewer',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const { token } = useAuthStore();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Nepodarilo sa vytvoriť používateľa');
      }

      onSuccess();
    } catch (err: any) {
      setError(getErrorMessage(err, 'Nepodarilo sa vytvoriť používateľa.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="dialog small-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h3>➕ Pridať používateľa</h3>
          <button className="dialog-close" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit} className="user-form">
          {error && <div className="error-message">⚠️ {error}</div>}

          <div className="form-group">
            <label>Email *</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label>Používateľské meno *</label>
            <input
              type="text"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label>Celé meno</label>
            <input
              type="text"
              value={formData.full_name}
              onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
            />
          </div>

          <div className="form-group">
            <label>Heslo *</label>
            <input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required
              minLength={6}
            />
          </div>

          <div className="form-group">
            <label>Rola *</label>
            <select
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value as UserRole })}
            >
              <option value="viewer">Pozorovateľ</option>
              <option value="install_tech">Montážny technik</option>
              <option value="service_tech">Servisný technik</option>
              <option value="engineer">Inžinier</option>
              <option value="admin">Administrátor</option>
              <option value="guest">Hosť</option>
            </select>
          </div>

          <div className="dialog-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Zrušiť
            </button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Vytváram...' : 'Vytvoriť'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Edit User Dialog Component
interface EditUserDialogProps {
  user: User;
  onClose: () => void;
  onSuccess: () => void;
}

const EditUserDialog: React.FC<EditUserDialogProps> = ({ user, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    full_name: user.full_name || '',
    role: user.role,
    is_active: user.is_active,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const { token } = useAuthStore();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/auth/users/${user.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Nepodarilo sa upraviť používateľa');
      }

      onSuccess();
    } catch (err: any) {
      setError(getErrorMessage(err, 'Nepodarilo sa upraviť používateľa.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="dialog small-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h3>✏️ Upraviť používateľa</h3>
          <button className="dialog-close" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit} className="user-form">
          {error && <div className="error-message">⚠️ {error}</div>}

          <div className="form-group">
            <label>Email</label>
            <input type="email" value={user.email} disabled />
          </div>

          <div className="form-group">
            <label>Používateľské meno</label>
            <input type="text" value={user.username} disabled />
          </div>

          <div className="form-group">
            <label>Celé meno</label>
            <input
              type="text"
              value={formData.full_name}
              onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
            />
          </div>

          <div className="form-group">
            <label>Rola</label>
            <select
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value as UserRole })}
            >
              <option value="viewer">Pozorovateľ</option>
              <option value="install_tech">Montážny technik</option>
              <option value="service_tech">Servisný technik</option>
              <option value="engineer">Inžinier</option>
              <option value="admin">Administrátor</option>
              <option value="guest">Hosť</option>
            </select>
          </div>

          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              />
              {' '}Aktívny účet
            </label>
          </div>

          <div className="dialog-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Zrušiť
            </button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Ukladám...' : 'Uložiť'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Manage Modules Dialog Component
interface ManageModulesDialogProps {
  user: User;
  onClose: () => void;
  onSuccess: () => void;
}

interface AvailableModule {
  module: string;
  display_name: string;
  description?: string;
}

/** Moduly, ktoré sa prideliť nedajú — tie oblasti zostávajú len adminovi. */
const LEN_ADMIN = ['user_management'];

const VOLBY: { hodnota: ModuleAccess | ''; popis: string }[] = [
  { hodnota: '', popis: 'Bez prístupu' },
  { hodnota: 'read', popis: 'Čítanie' },
  { hodnota: 'edit', popis: 'Úprava' },
];

const ManageModulesDialog: React.FC<ManageModulesDialogProps> = ({ user, onClose, onSuccess }) => {
  const [availableModules, setAvailableModules] = useState<AvailableModule[]>([]);
  const [urovne, setUrovne] = useState<Record<string, ModuleAccess>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { token } = useAuthStore();

  useEffect(() => {
    fetchModules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchModules = async () => {
    try {
      setLoading(true);

      const hlavicky = { 'Authorization': `Bearer ${token}` };

      // Dostupné moduly a to, čo má používateľ pridelené, sa ťahá naraz.
      // Pridelené moduly čítame z vlastného endpointu, nie zo zoznamu
      // používateľov — ten ich kedysi neposielal a dialóg potom svietil
      // prázdny, akoby sa nič neuložilo.
      const [availableResponse, mojeResponse] = await Promise.all([
        fetch(`${API_URL}/api/users/modules/available`, { headers: hlavicky }),
        fetch(`${API_URL}/api/users/${user.id}/modules`, { headers: hlavicky }),
      ]);

      if (!availableResponse.ok) {
        throw new Error('Nepodarilo sa načítať dostupné moduly');
      }

      setAvailableModules(await availableResponse.json());

      if (mojeResponse.ok) {
        const moje = await mojeResponse.json();
        const mapa: Record<string, ModuleAccess> = {};
        moje.forEach((m: { module: string; access_level?: ModuleAccess }) => {
          mapa[m.module] = m.access_level || 'edit';
        });
        setUrovne(mapa);
      } else {
        // Záloha pre starší backend, ktorý tento endpoint ešte nemá.
        const mapa: Record<string, ModuleAccess> = {};
        (user.modules || []).forEach((m) => { mapa[m] = 'edit'; });
        setUrovne(mapa);
      }

    } catch (err: any) {
      setError(getErrorMessage(err, 'Nepodarilo sa načítať moduly.'));
    } finally {
      setLoading(false);
    }
  };

  const nastavUroven = (moduleName: string, uroven: ModuleAccess | '') => {
    setUrovne((predtym) => {
      const nove = { ...predtym };
      if (uroven === '') {
        delete nove[moduleName];
      } else {
        nove[moduleName] = uroven;
      }
      return nove;
    });
  };

  const pocty = {
    upravuje: Object.values(urovne).filter((u) => u === 'edit').length,
    vidi: Object.values(urovne).filter((u) => u === 'read').length,
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/users/${user.id}/modules`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          modules: Object.entries(urovne).map(([module, access_level]) => ({
            module,
            access_level,
          })),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Nepodarilo sa uložiť moduly');
      }

      onSuccess();
    } catch (err: any) {
      setError(getErrorMessage(err, 'Nepodarilo sa uložiť moduly.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="dialog small-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h3>🧩 Spravovať moduly - {user.username}</h3>
          <button className="dialog-close" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit} className="user-form">
          {error && <div className="error-message">⚠️ {error}</div>}

          {loading ? (
            <div className="loading">Načítavam moduly...</div>
          ) : (
            <div className="form-group">
              <label>Moduly a úroveň prístupu:</label>
              {user.role === 'admin' && (
                <div className="moduly-poznamka">
                  Administrátor má všetky moduly z roly — tu nastavené hodnoty sa
                  naň nevzťahujú.
                </div>
              )}
              <div className="moduly-zoznam">
                {availableModules.map((module) => {
                  const uroven = urovne[module.module] || '';
                  const lenAdmin = LEN_ADMIN.includes(module.module);
                  return (
                    <div
                      key={module.module}
                      className={`modul-riadok${uroven ? ' modul-riadok--aktivny' : ''}`}
                    >
                      <div className="modul-popis">
                        <span className="modul-nazov">{module.display_name}</span>
                        {module.description && (
                          <span className="modul-detail">{module.description}</span>
                        )}
                      </div>
                      <div className="modul-volby">
                        {VOLBY.map((volba) => (
                          <label
                            key={volba.hodnota || 'ziadny'}
                            className={`modul-volba${uroven === volba.hodnota ? ' modul-volba--vybrana' : ''}`}
                            title={lenAdmin ? 'Táto oblasť zostáva len adminovi' : undefined}
                          >
                            <input
                              type="radio"
                              name={`modul-${module.module}`}
                              checked={uroven === volba.hodnota}
                              disabled={lenAdmin}
                              onChange={() => nastavUroven(module.module, volba.hodnota)}
                            />
                            <span>{volba.popis}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="moduly-suhrn">
                Upravuje: <strong>{pocty.upravuje}</strong> &nbsp;·&nbsp;
                Vidí: <strong>{pocty.vidi}</strong> &nbsp;·&nbsp;
                Bez prístupu: <strong>{availableModules.length - pocty.upravuje - pocty.vidi}</strong>
              </div>
            </div>
          )}

          <div className="dialog-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Zrušiť
            </button>
            <button type="submit" className="btn-primary" disabled={saving || loading}>
              {saving ? 'Ukladám...' : 'Uložiť'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

